// Banners (IMPLEMENTATION §3 M7 RuleBanner, UX §4 3:00+ "Rule banner (once, then permanent in the Shop footer
// and on machine cards)", §0.4 u21): the one-time rule plaque "Machines keep the tier: merge first = more
// stuff." over the top of the board when the first machine is built (banner.ruleMs), and the v0.1 completion
// card (u21 banner: automated income reached, Export telemetry, Keep playing). Plus the ⟳ handle a freshly
// placed building shows for input.rotateHandleMs (R14, §2.8 step 4), in both modes. And the building how-to
// card (ui/howto.ts): what a newly unlocked building does, in numbered steps, over the top of the board.
import { cellCenter } from '../../layout/computeLayout.ts';
import { canRotate, completion } from '../../sim/query.ts';
import { fillCopy } from '../copy.ts';
import { shellCtx } from '../ctx.ts';
import { perSec } from '../format.ts';
import { howtoSteps } from '../howto.ts';
import { bannerSig, draggingSig, howtoSig, layoutSig, rotateHandleSig } from '../state.ts';
import { Icon } from './Icon.tsx';
import { CopyBox } from './Settings.tsx';

export function RuleBanner() {
  const l = layoutSig.value;
  const b = bannerSig.value;
  if (!l || b === null) return null;
  const ctx = shellCtx();
  const copy = ctx.data().copy;
  const gutter = ctx.data().ui.layout.gutter;
  const style = {
    left: `${l.column.x + gutter}px`,
    width: `${l.column.w - 2 * gutter}px`,
    top: `${l.boardRegion.y}px`,
  };
  if (b === 'rule')
    return (
      <div class="rule-banner" style={style} data-testid="rule-banner" role="status">
        {copy.rule.banner ?? ''}
      </div>
    );
  const f = ctx.shell.fmt();
  const done = completion(ctx.state(), ctx.data().sim);
  return (
    <div class="complete-banner ctl" style={style} data-testid="complete-banner" role="dialog">
      <b class="complete-title">{copy.complete.v01 ?? ''}</b>
      <p>{fillCopy(copy.complete.v01Body ?? '', { income: perSec(f, done.autoPerSec) })}</p>
      <div class="complete-actions">
        <button
          type="button"
          class="btn btn-buy ctl"
          data-hit="complete-export"
          onClick={() => void ctx.actions.exportTelemetry()}
        >
          {copy.complete.v01Export ?? ''}
        </button>
        <button
          type="button"
          class="btn ctl"
          data-hit="complete-close"
          onClick={() => ctx.shell.dismissBanner()}
        >
          {copy.complete.close ?? ''}
        </button>
      </div>
      {/* share and download both failed: the CSV to copy, here where the export was started */}
      <CopyBox />
    </div>
  );
}

export function HowToCard() {
  const l = layoutSig.value;
  const type = howtoSig.value;
  if (!l || type === null || draggingSig.value) return null;
  const ctx = shellCtx();
  const data = ctx.data();
  const copy = data.copy;
  const s = ctx.state();
  const steps = howtoSteps(copy, data.sim, type, s.progress.firstBuilt.includes(type));
  if (steps.length === 0) return null;
  const gutter = data.ui.layout.gutter;
  return (
    <div
      class="howto-card ctl"
      style={{
        left: `${l.column.x + gutter}px`,
        width: `${l.column.w - 2 * gutter}px`,
        top: `${l.boardRegion.y}px`,
      }}
      data-testid="howto-card"
      data-howto={type}
      role="dialog"
    >
      <div class="howto-head">
        <Icon icon={{ k: 'building', type, ore: null, level: 1 }} px={data.ui.cards.iconPx} />
        <b class="howto-title">
          {fillCopy(copy.howto.title ?? '', { name: ctx.shell.fmt().names.building(type, null) })}
        </b>
      </div>
      <ol class="howto-steps">
        {steps.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ol>
      <button
        type="button"
        class="btn btn-buy ctl"
        data-hit="howto-close"
        onClick={() => {
          ctx.actions.click();
          ctx.shell.dismissHowto();
        }}
      >
        {copy.howto.gotIt ?? ''}
      </button>
    </div>
  );
}

export function RotateHandle() {
  const l = layoutSig.value;
  const h = rotateHandleSig.value;
  if (!l || h === null || draggingSig.value) return null;
  const ctx = shellCtx();
  const b = ctx.state().buildings.find((x) => x.id === h.bid);
  if (!b || !canRotate(ctx.state(), ctx.data().sim, b.id)) return null;
  const c = cellCenter(l, b.x, b.y);
  const size = ctx.data().ui.hit.min;
  const off = l.cellCss / 2;
  // at the cell's top-right corner, kept inside the column (a right-edge cell must not push it off-screen)
  const left = Math.min(Math.max(c.x + off - size / 2, l.column.x), l.column.x + l.column.w - size);
  const top = Math.max(c.y - off - size / 2, l.column.y);
  return (
    <button
      type="button"
      class="rotate-handle ctl"
      data-hit="rotate-handle"
      data-testid="rotate-handle"
      aria-label={ctx.data().copy.build.rotate ?? ''}
      style={{
        left: `${left}px`,
        top: `${top}px`,
        width: `${size}px`,
        height: `${size}px`,
      }}
      onClick={() => {
        ctx.actions.click();
        ctx.actions.dispatch({ c: 'rotate', bid: b.id });
      }}
    >
      ⟳
    </button>
  );
}
